import { z } from "zod";

// --- Custom document property names injected by the ICI platform (WopiModel.cs) ---
export const WOPI_PROPS = {
    wopiMode: "WOPI_MODE", // "true" when the doc is opened through the ICI WOPI host
    iciUrl: "ICI_URL", // BaseURI — one of our app URLs, e.g. https://app.cust-staging-us.vera.icertis.com/
    iciApiUrl: "ICI_API_URL", // ICI platform API base, e.g. https://api.cust-staging-us.vera.icertis.com/api
    addinUiVisibility: "ADDIN_UI_VISIBILITY", // pipe-delimited flags "<teamPanel>|<approveReject>|<saveDiscard>" gating add-in UI regions
} as const;

// --- postMessage wire contract (platform-confirmed) ---
export const WOPI_MESSAGE = {
    request: "SendUrl", // add-in → ICI: window.parent.parent.postMessage({ type: "SendUrl" }, "*")
    response: "ReadUrl", // ICI → add-in: { type: "ReadUrl", data: { Url } }
    tokenRequest: "SendToken", // add-in → ICI: window.parent.parent.postMessage({ type: "SendToken" }, "*")
    tokenResponse: "ReadToken", // ICI → add-in: { type: "ReadToken", data: { ici_token, ... } }
} as const;

// The ICI platform is .NET and serializes with PascalCase keys, so the reply is
// `{ type: "ReadUrl", data: { Url } }`. Accept either casing and normalize to `url`.
export const ReadUrlMessageSchema = z.object({
    type: z.literal(WOPI_MESSAGE.response),
    data: z
        .object({
            Url: z.string().optional(),
            url: z.string().optional(),
        })
        .transform((d) => ({ url: d.Url ?? d.url }))
        .refine((d): d is { url: string } => d.url != null, {
            message: "ReadUrl reply missing url",
        }),
});

// The ICI page replies to `SendToken` with `{ type: "ReadToken", data: { ici_token,
// ici_requestverificationtoken, ici_refreshtoken, ici_accesstokenTTL } }`. The platform
// wraps some values in literal quotes, so strip them and normalize to camelCase.
export const ReadTokenMessageSchema = z.object({
    type: z.literal(WOPI_MESSAGE.tokenResponse),
    data: z
        .object({
            ici_token: z.string().optional(),
            ici_requestverificationtoken: z.string().optional(),
            ici_refreshtoken: z.string().optional(),
            ici_accesstokenTTL: z.union([z.string(), z.number()]).optional(),
        })
        .transform((d) => ({
            token: d.ici_token?.replaceAll("\"", ""),
            requestVerificationToken: d.ici_requestverificationtoken?.replaceAll("\"", ""),
            refreshToken: d.ici_refreshtoken?.replaceAll("\"", ""),
            accessTokenTtl: d.ici_accesstokenTTL,
        }))
        .refine((d): d is WopiToken => d.token != null, {
            message: "ReadToken reply missing ici_token",
        }),
});

export interface WopiToken {
    token: string;
    requestVerificationToken: string | undefined;
    refreshToken: string | undefined;
    accessTokenTtl: string | number | undefined;
}

// The proxy envelope returned by POST /api/v1/wopi/ici-proxy: the composed target
// url, the ICI HTTP status, and the raw ICI response text (each action parses `body`
// with its own schema below).
export const IciProxyResponseSchema = z.object({
    url: z.string(),
    status: z.number(),
    ok: z.boolean(),
    body: z.string(),
});
export type IciProxyResponse = z.infer<typeof IciProxyResponseSchema>;

// ICI workflow-action payloads (`WFAction`, ResumeWorkFlowMobile bodies) are opaque
// JSON we forward verbatim; model them as JSON rather than reaching for `unknown`.
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
    z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.null(),
        z.array(JsonValueSchema),
        z.record(z.string(), JsonValueSchema),
    ])
);

// The ICI serializer emits `null` wherever a value is absent — collections
// (confirmed live: `"ActionUsers":null`) and scalars (confirmed live:
// `"IsNoteMandatory":null`) alike — so every ICI-sourced field must tolerate null.
// Flags and text coalesce to the platform defaults; collections normalize to []
// so the deep-equal poll skip sees one shape.
function nullableArray<T extends z.ZodType>(item: T) {
    return z
        .array(item)
        .nullish()
        .transform((value) => value ?? []);
}
const NullableFlagSchema = z
    .boolean()
    .nullish()
    .transform((value) => value ?? false);
const NullableTextSchema = z
    .string()
    .nullish()
    .transform((value) => value ?? "");

// --- GetAvailableVisibleActions (Instance/GetAvailableVisibleActions) ---
export const IciWorkflowActionSchema = z.object({
    DisplayString: z.string().nullish(),
    WFAction: JsonValueSchema.nullish(),
});
export const IciRoleActionMapSchema = z.object({
    IsNoteAvailable: NullableFlagSchema,
    IsNoteMandatory: NullableFlagSchema,
    ActionDisplayString: z.string().nullish(),
});
export const IciVisibleActionSchema = z.object({
    ActionName: z.string().nullish(),
    WorkflowAction: IciWorkflowActionSchema.nullish(),
    RoleActionMap: IciRoleActionMapSchema.nullish(),
});
export const IciVisibleActionsSchema = z.array(IciVisibleActionSchema);
export type IciVisibleAction = z.infer<typeof IciVisibleActionSchema>;

// --- GetAgreement (Agreement/GetAgreement) — only the workflow status is read ---
export const AgreementSchema = z.object({
    SysId: z.string().nullish(),
    EntityName: z.string().nullish(),
    Status: z.string().nullish(),
    LastStatus: z.string().nullish(),
    Version: z.number().nullish(),
});
export type Agreement = z.infer<typeof AgreementSchema>;

// --- GetAssociationInstances (Instance/GetAssociationInstances) — team members ---
export const TeamMemberSchema = z.object({
    UserIdentifier: z.number().nullish(),
    RoleName: z.string().nullish(),
    IsGroup: NullableFlagSchema,
    UserEmailId: z.string().nullish(),
    UserName: z.string().nullish(),
});
export const TeamMembersSchema = z.array(TeamMemberSchema);
export type TeamMember = z.infer<typeof TeamMemberSchema>;

// --- GetCurrentUser (User/GetCurrentUser) — session user identity ---
export const CurrentUserSchema = z.object({
    Identifier: z.number().nullish(),
    FullName: z.string().nullish(),
    EmailID: z.string().nullish(),
    TypeOfUser: z.string().nullish(),
});
export type CurrentUser = z.infer<typeof CurrentUserSchema>;

// --- GetWopiPanelDetails (Agreement/GetWopiPanelDetails) — presence poll ---
export const WopiParticipatedUserSchema = z.object({
    FullName: z.string().nullish(),
    Email: z.string().nullish(),
});
export const WopiActionUserSchema = z.object({
    UserName: z.string().nullish(),
    ActionUserEmailID: z.string().nullish(),
    Useridentifier: z.number().nullish(),
    StepOrder: z.number().nullish(),
    ActionState: z.string().nullish(),
    Decision: z.string().nullish(),
    RoleName: z.string().nullish(),
    IsActionDeprecated: NullableFlagSchema,
    hasComments: NullableFlagSchema,
});
export const WopiActionUserGroupSchema = z.object({
    TeamMemberId: z.string().nullish(),
    TeamMemberUserName: z.string().nullish(),
    StepOrder: z.number().nullish(),
    IsGroup: NullableFlagSchema,
    ActionUsers: nullableArray(WopiActionUserSchema),
});
export const WopiPanelDetailsSchema = z.object({
    ParticipatedUsers: nullableArray(WopiParticipatedUserSchema),
    ActionUsers: nullableArray(WopiActionUserGroupSchema),
    IsWopiSessionEnding: NullableFlagSchema,
    IsWopiAddButtonVisible: NullableFlagSchema,
    IsWopiSessionDiscarded: NullableFlagSchema,
});
export type WopiPanelDetails = z.infer<typeof WopiPanelDetailsSchema>;

// --- SaveWithDocument response — a List<ActionValidator> ---
// Entries with ActionValidatorType 0 are blocking rejections (the save created no
// version, e.g. "Select a version label."); type-1 entries are informational
// change-summary rows (DocumentNotesValidation, MultipleFontsInFile, …).
export const ActionValidatorSchema = z.object({
    // An explicit false marks the failure; an absent flag does not (matching the
    // platform's `ValidateSuccessful == false` check).
    ValidateSuccessful: z.boolean().nullish(),
    ValidationStatus: z.string().nullish(),
    ValidationMessage: z.string().nullish(),
    ActionValidatorType: z.number().nullish(),
});
export const ActionValidatorsSchema = z.array(ActionValidatorSchema);
export type ActionValidator = z.infer<typeof ActionValidatorSchema>;

export const ContentControlDifferenceSchema = z.object({
    Action: NullableTextSchema,
    DisplayName: NullableTextSchema,
    TagCount: z.number().nullish(),
    DocumentControlType: NullableTextSchema,
});
export const ContentControlDifferencesSchema = z.array(ContentControlDifferenceSchema);
export type ContentControlDifference = z.infer<typeof ContentControlDifferenceSchema>;

// --- IsLastApprover (Agreement/IsLastApprover) → { success, data } ---
export const IsLastApproverSchema = z.object({
    success: z.boolean().nullish(),
    data: NullableFlagSchema,
});

// --- Configurations (Configurations?configKeys=…) → { key: value } ---
export const ConfigurationsSchema = z.record(z.string(), z.string());

// --- Version labels for Save New Version ---
// The `EnableDocumentVersionLabels` Configurations value is itself JSON whose flags
// are JSON-encoded booleans, e.g. `{"EnableDocumentVersionLabels":"true","IsMandatory":"true"}`.
const EncodedFlagSchema = z
    .union([z.boolean(), z.string()])
    .nullish()
    .transform((value) => value === true || (typeof value === "string" && value.trim().toLowerCase() === "true"));

export const VersionLabelsConfigSchema = z.object({
    EnableDocumentVersionLabels: EncodedFlagSchema,
    IsMandatory: EncodedFlagSchema,
});

// --- GetVersionLabels (VersionLabelMaster/GetVersionLabels) ---
export const VersionLabelSchema = z.object({
    SysId: z.string(),
    DisplayName: NullableTextSchema,
    IsDeprecated: NullableFlagSchema,
});
export const VersionLabelsSchema = z.array(VersionLabelSchema);
export type VersionLabel = z.infer<typeof VersionLabelSchema>;

// --- GetReasons (ReasonMaster/GetReasons) → normalized reason codes ---
export const ReasonCodeSchema = z
    .object({
        SysId: z.string(),
        ReasonCode: NullableTextSchema,
        Heading: NullableTextSchema,
    })
    .transform((r) => ({
        sysId: r.SysId,
        reasonCode: r.ReasonCode,
        heading: r.Heading,
        reason: `${r.ReasonCode}-${r.Heading}`,
    }));
export const ReasonCodesSchema = z.array(ReasonCodeSchema);
export type ReasonCode = z.infer<typeof ReasonCodeSchema>;
